import * as Yup from 'yup';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INDIAN_MOBILE_REGEX = /^[6-9][0-9]{9}$/;
const INDIAN_PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
/** Standard: KA01AB1234 / TN38ABC1234. Bharat series: 22BH1234AA */
const INDIAN_VEHICLE_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z]{1,3}[0-9]{4}$/;
const BHARAT_SERIES_VEHICLE_REGEX = /^[0-9]{2}BH[0-9]{4}[A-Z]{2}$/;
/** Current smart card: TN0720240001234. Older: TN07220001234 */
const INDIAN_DL_REGEX = /^[A-Z]{2}[0-9]{13}$/;
const INDIAN_DL_LEGACY_REGEX = /^[A-Z]{2}[0-9]{11}$/;
/** Indian PIN / zipcode: 6 digits, first digit 1-9 */
const INDIAN_PINCODE_REGEX = /^[1-9][0-9]{5}$/;

export const normalizeMobile = (value) => {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }
  return digits;
};

export const isFakeMobileNumber = (mobile) => {
  if (/^(\d)\1{9}$/.test(mobile)) {
    return true;
  }

  const digits = mobile.split('').map(Number);
  let ascending = true;
  let descending = true;

  for (let i = 1; i < digits.length; i += 1) {
    if (digits[i] !== digits[i - 1] + 1) ascending = false;
    if (digits[i] !== digits[i - 1] - 1) descending = false;
  }

  if (ascending || descending) {
    return true;
  }

  if (/^(\d{2})\1{4}$/.test(mobile) || /^(\d{5})\1$/.test(mobile)) {
    return true;
  }

  return false;
};

/** Same rules as backend parseAuthIdentifier / mobileValidation.validateIndianMobile */
export const validateIndianMobile = (value) => {
  const normalizedMobile = normalizeMobile(value);

  if (!INDIAN_MOBILE_REGEX.test(normalizedMobile)) {
    return { valid: false, message: 'Enter valid 10-digit mobile number', normalizedMobile: null };
  }

  if (isFakeMobileNumber(normalizedMobile)) {
    return { valid: false, message: 'Please enter a valid mobile number', normalizedMobile: null };
  }

  return { valid: true, message: null, normalizedMobile };
};

export const indianMobileSchema = (requiredMessage = 'Mobile No is required') =>
  Yup.string()
    .required(requiredMessage)
    .test('indian-mobile', function indianMobileTest(value) {
      const result = validateIndianMobile(value);
      if (result.valid) {
        return true;
      }
      return this.createError({ message: result.message });
    });

export const normalizePan = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);

/** Indian PAN: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F) */
export const validateIndianPan = (value) => {
  const normalizedPan = normalizePan(value);

  if (!normalizedPan) {
    return { valid: false, message: 'PAN No is required', normalizedPan: null };
  }

  if (!INDIAN_PAN_REGEX.test(normalizedPan)) {
    return {
      valid: false,
      message: 'Enter a valid PAN (e.g. ABCDE1234F)',
      normalizedPan: null,
    };
  }

  return { valid: true, message: null, normalizedPan };
};

export const indianPanSchema = (requiredMessage = 'PAN No is required') =>
  Yup.string()
    .required(requiredMessage)
    .test('indian-pan', function indianPanTest(value) {
      const result = validateIndianPan(value);
      if (result.valid) {
        return true;
      }
      return this.createError({ message: result.message });
    });

export const normalizeVehicleNumber = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 11);

/** Indian vehicle registration: KA01AB1234 or Bharat series 22BH1234AA */
export const validateIndianVehicleNumber = (value) => {
  const normalizedVehicleNumber = normalizeVehicleNumber(value);

  if (!normalizedVehicleNumber) {
    return {
      valid: false,
      message: 'Vehicle Number is required',
      normalizedVehicleNumber: null,
    };
  }

  if (
    !INDIAN_VEHICLE_REGEX.test(normalizedVehicleNumber) &&
    !BHARAT_SERIES_VEHICLE_REGEX.test(normalizedVehicleNumber)
  ) {
    return {
      valid: false,
      message: 'Enter a valid vehicle number (e.g. KA01AB1234)',
      normalizedVehicleNumber: null,
    };
  }

  return { valid: true, message: null, normalizedVehicleNumber };
};

export const indianVehicleNumberSchema = (
  requiredMessage = 'Vehicle Number is required'
) =>
  Yup.string()
    .required(requiredMessage)
    .test('indian-vehicle-number', function indianVehicleNumberTest(value) {
      const result = validateIndianVehicleNumber(value);
      if (result.valid) {
        return true;
      }
      return this.createError({ message: result.message });
    });

export const normalizeLicenceNumber = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 15);

/** Indian DL: 2-letter state + RTO + year + unique no. e.g. TN0720240001234 */
export const validateIndianLicenceNumber = (value) => {
  const normalizedLicenceNumber = normalizeLicenceNumber(value);

  if (!normalizedLicenceNumber) {
    return {
      valid: false,
      message: 'Licence No is required',
      normalizedLicenceNumber: null,
    };
  }

  if (
    !INDIAN_DL_REGEX.test(normalizedLicenceNumber) &&
    !INDIAN_DL_LEGACY_REGEX.test(normalizedLicenceNumber)
  ) {
    return {
      valid: false,
      message: 'Enter a valid licence number (e.g. TN0720240001234)',
      normalizedLicenceNumber: null,
    };
  }

  return { valid: true, message: null, normalizedLicenceNumber };
};

export const indianLicenceNumberSchema = (
  requiredMessage = 'Licence No is required'
) =>
  Yup.string()
    .required(requiredMessage)
    .test('indian-licence-number', function indianLicenceNumberTest(value) {
      const result = validateIndianLicenceNumber(value);
      if (result.valid) {
        return true;
      }
      return this.createError({ message: result.message });
    });

export const normalizePincode = (value) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, 6);

/** Indian PIN / zipcode: exactly 6 digits, cannot start with 0 */
export const validateIndianPincode = (value) => {
  const normalizedPincode = normalizePincode(value);

  if (!normalizedPincode) {
    return { valid: false, message: 'Zipcode is required', normalizedPincode: null };
  }

  if (!INDIAN_PINCODE_REGEX.test(normalizedPincode)) {
    return {
      valid: false,
      message: 'Enter a valid 6-digit zipcode',
      normalizedPincode: null,
    };
  }

  return { valid: true, message: null, normalizedPincode };
};

/** Verifies Indian PIN exists via India Post data (Zippopotam). */
export const verifyIndianPincodeExists = async (value) => {
  const formatResult = validateIndianPincode(value);
  if (!formatResult.valid) {
    return { ...formatResult, normalizedPincode: null };
  }

  try {
    const response = await fetch(
      `https://api.zippopotam.us/in/${formatResult.normalizedPincode}`,
    );

    if (response.status === 404) {
      return {
        valid: false,
        message: 'This zipcode does not exist',
        normalizedPincode: null,
      };
    }

    if (!response.ok) {
      return {
        valid: false,
        message: 'Unable to verify zipcode. Please try again.',
        normalizedPincode: null,
      };
    }

    const data = await response.json();
    if (data?.places?.length > 0) {
      return {
        valid: true,
        message: null,
        normalizedPincode: formatResult.normalizedPincode,
      };
    }

    return {
      valid: false,
      message: 'This zipcode does not exist',
      normalizedPincode: null,
    };
  } catch {
    return {
      valid: false,
      message: 'Unable to verify zipcode. Please try again.',
      normalizedPincode: null,
    };
  }
};

export const detectIdentifierType = (value) => {
  const trimmed = String(value || '').trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('@')) {
    return EMAIL_REGEX.test(trimmed) ? 'email' : null;
  }

  const mobileValidation = validateIndianMobile(trimmed);
  if (!mobileValidation.valid) {
    return null;
  }

  return 'phone';
};

export const maskIdentifier = (value, type) => {
  const trimmed = String(value || '').trim();

  if (type === 'email') {
    const [localPart, domain] = trimmed.split('@');
    if (!domain) {
      return trimmed;
    }
    const visible = localPart.slice(0, 2);
    return `${visible}${'*'.repeat(Math.max(localPart.length - 2, 3))}@${domain}`;
  }

  const digits = normalizeMobile(trimmed);
  return `******${digits.slice(-4)}`;
};

export const identifierSchema = Yup.string()
  .required('Email or phone number is required.')
  .test('valid-identifier', 'Enter a valid email address or phone number.', (value) => Boolean(detectIdentifierType(value)));
